import { Button, Flex, Icon, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, useDisclosure } from '@chakra-ui/react';
import { BiRepeat } from 'react-icons/bi';
import { LuSparkles } from 'react-icons/lu';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useOptimizationResult } from '../../api/optimization';
import { useRequiredParams } from '../../utils/useRequiredParams';
import { PageHeader } from '../../widgets/PageHeader';
import { AiEvaluationDrawer } from './AiEvaluationDrawer';
import { OptimizationSummary } from './OptimizationSummary';
import { TradeDetails } from './TradeDetails/TradeDetails';
import { TradeErrors } from './TradeErrors';
import { TradeExecution } from './TradeExecution/TradeExecution';

export const OptimizationResults = () => {
    const navigate = useNavigate();
    const { optimizationId } = useRequiredParams(['optimizationId']);
    const { data: optimizationResults, isPending } = useOptimizationResult(Number(optimizationId));
    const { isOpen: isEvalOpen, onOpen: openEval, onClose: closeEval } = useDisclosure();

    const [searchParams, setSearchParams] = useSearchParams();
    const tabIdx = Number(searchParams.get('tabIdx') || 0);

    return (
        <>
            <PageHeader title={`Optimization #${optimizationId} results`}>
                <Flex gap={2}>
                    <Button size="sm" leftIcon={<Icon as={LuSparkles} />} onClick={openEval}>
                        Evaluate with AI
                    </Button>
                    <Button
                        size="sm"
                        leftIcon={<Icon as={BiRepeat} />}
                        onClick={() => navigate(`../builder?rerun=${optimizationId.toString()}`)}
                    >
                        Rerun optimization
                    </Button>
                </Flex>
            </PageHeader>
            <AiEvaluationDrawer isOpen={isEvalOpen} onClose={closeEval} optimizationId={optimizationId} />
            <Stack>
                <OptimizationSummary optimizationMeta={optimizationResults?.meta} isLoading={isPending} />
                <Tabs
                    colorScheme="teal"
                    index={tabIdx}
                    onChange={(idx) => {
                        setSearchParams(
                            (params) => {
                                params.set('tabIdx', idx.toString());
                                if (idx !== 2) {
                                    params.delete('pId');
                                    params.delete('investmentAlgo');
                                }

                                return params;
                            },
                            { replace: true },
                        );
                    }}
                    isLazy
                    lazyBehavior="keepMounted"
                >
                    <TabList>
                        <Tab>Trade Execution</Tab>
                        <Tab
                            color={optimizationResults?.errors.length ? 'red.400' : undefined}
                            isDisabled={!optimizationResults?.errors.length}
                        >
                            {optimizationResults?.errors.length
                                ? `Errors (${optimizationResults?.errors.length})`
                                : 'Errors'}
                        </Tab>
                        <Tab ml="auto">Advanced</Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel px={0}>
                            <TradeExecution
                                trades={optimizationResults?.trades}
                                isLoading={isPending}
                                optimizationMeta={optimizationResults?.meta}
                            />
                        </TabPanel>
                        <TabPanel px={0}>
                            <TradeErrors errors={optimizationResults?.errors} isLoading={isPending} />
                        </TabPanel>
                        <TabPanel px={0}>
                            <TradeDetails optimizationMeta={optimizationResults?.meta} isLoading={isPending} />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Stack>
        </>
    );
};
